import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, FileText, Pencil, Plus, RefreshCw, Upload, WalletCards, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Pill } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { api, toastError } from "../../lib/api";
import { canAtLeast, useEffectiveRole } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";

type Claim = {
  id: string; claimant_user_id: string; claimant_name: string; category: string; expense_date: string; currency: string;
  claimed_km: number; km_rate: number; mileage_amount: number; expense_amount: number;
  total_amount: number; status: string; notes: string; receipt_id?: string; receipt_name?: string;
  review_reason?: string; payment_reference?: string; visit_id?: string; visit_ids?: string[]; contact_id?: string; version: number;
  period_start?: string; period_end?: string; visit_snapshots?: VisitSnapshot[];
};
type Visit = { id: string; title: string; event_type_code?: string; start_at?: string; start_date?: string; status?: string; contact_id?: string; organization_id?: string };
type VisitSnapshot = { visit_id: string; title?: string; contact_name?: string; organization_name?: string; visit_type: string; start_at?: string; start_date?: string; status: string };
type Contact = { id: string; first_name: string; last_name?: string };
type Organization = { id: string; name: string };
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ category: "mileage", expense_date: today(), period_start: today(), period_end: today(), currency: "CAD", claimed_km: "0", km_rate: "0.70", expense_amount: "0", visit_ids: [] as string[], contact_id: "", notes: "" });
const money = (amount: number, currency: string) => new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount || 0);

export default function ExpenseClaims() {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const manager = canAtLeast(role, "manager_trainer");
  const owner = canAtLeast(role, "owner_admin");
  const [items, setItems] = useState<Claim[]>([]);
  const [form, setForm] = useState(blank());
  const [editing, setEditing] = useState<Claim | null>(null);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState(manager ? "submitted" : "draft");
  const [busy, setBusy] = useState(false);
  const [reviewReasons, setReviewReasons] = useState<Record<string, string>>({});
  const [paymentRefs, setPaymentRefs] = useState<Record<string, string>>({});
  const [visits, setVisits] = useState<Visit[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/expense-claims", { params: { status: status === "all" ? "" : status } });
      setItems(data.items);
    } catch (error) { toastError(error); }
  }, [status]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    Promise.all([
      api.get("/crm/events", { params: { limit: 500 } }),
      api.get("/crm/contacts", { params: { limit: 500, archived: false } }),
      api.get("/crm/organizations", { params: { limit: 500, archived: false } }),
    ]).then(([eventsResponse, contactsResponse, organizationsResponse]) => {
      setVisits(eventsResponse.data.items || []);
      setContacts(contactsResponse.data || []);
      setOrganizations(organizationsResponse.data || []);
    }).catch(() => {
      setVisits([]);
      setContacts([]);
      setOrganizations([]);
    });
  }, []);

  const contactNames = useMemo(() => Object.fromEntries(
    contacts.map(contact => [contact.id, `${contact.first_name} ${contact.last_name || ""}`.trim()])
  ), [contacts]);
  const organizationNames = useMemo(() => Object.fromEntries(
    organizations.map(organization => [organization.id, organization.name])
  ), [organizations]);
  const eligibleVisits = useMemo(() => visits.filter(visit => {
    if (visit.status !== "completed") return false;
    const visitDay = String(visit.start_date || visit.start_at || "").slice(0, 10);
    return Boolean(visitDay && visitDay >= form.period_start && visitDay <= form.period_end);
  }), [visits, form.period_start, form.period_end]);
  const visitLabel = (visit: Visit) => {
    const who = [contactNames[visit.contact_id || ""], organizationNames[visit.organization_id || ""]].filter(Boolean).join(" — ") || visit.title;
    const type = String(visit.event_type_code || "visit").replaceAll("_", " ").replace(/\b\w/g, letter => letter.toUpperCase());
    const when = visit.start_at
      ? new Date(visit.start_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
      : visit.start_date || "Date unavailable";
    return `${who} · ${type} · ${when}`;
  };

  const totals = useMemo(() => items.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + item.total_amount; return acc;
  }, {} as Record<string, number>), [items]);

  const resetEditor = () => {
    setForm(blank());
    setReceipt(null);
    setEditing(null);
    setShowForm(false);
  };

  const beginEdit = (claim: Claim) => {
    setEditing(claim);
    setForm({
      category: claim.category,
      expense_date: claim.expense_date,
      currency: claim.currency,
      claimed_km: String(claim.claimed_km || 0),
      km_rate: String(claim.km_rate || 0.70),
      expense_amount: String(claim.expense_amount || 0),
      period_start: claim.period_start || claim.expense_date,
      period_end: claim.period_end || claim.expense_date,
      visit_ids: claim.visit_ids || (claim.visit_id ? [claim.visit_id] : []),
      contact_id: claim.contact_id || "",
      notes: claim.notes || "",
    });
    setReceipt(null);
    setShowForm(true);
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        claimed_km: form.category === "mileage" ? Number(form.claimed_km) : 0,
        km_rate: form.category === "mileage" ? Number(form.km_rate) : 0,
        expense_amount: form.category === "mileage" ? 0 : Number(form.expense_amount),
        visit_id: null,
        visit_ids: form.visit_ids,
        period_start: form.period_start,
        period_end: form.period_end,
        contact_id: form.contact_id || null,
      };
      const { data: claim } = editing
        ? await api.patch(`/tenant/expense-claims/${editing.id}`, { ...payload, version: editing.version })
        : await api.post("/tenant/expense-claims", payload);
      if (receipt) {
        const receiptPayload = new FormData();
        receiptPayload.append("receipt", receipt);
        try {
          await api.post(`/tenant/expense-claims/${claim.id}/receipt`, receiptPayload);
        } catch (receiptError) {
          toast.error("The draft was saved, but the receipt did not upload. Edit the draft and attach it again.");
          await load();
          setEditing(claim);
          return;
        }
      }
      toast.success(editing ? "Reimbursement draft updated." : "Reimbursement request saved as draft.");
      resetEditor();
      if (status === "draft") await load();
      else setStatus("draft");
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const action = async (claim: Claim, name: "submit" | "approve" | "reject" | "reimburse") => {
    setBusy(true);
    try {
      if (name === "submit") await api.post(`/tenant/expense-claims/${claim.id}/submit`);
      if (name === "approve" || name === "reject") await api.post(`/tenant/expense-claims/${claim.id}/review`, {
        decision: name === "approve" ? "approved" : "rejected", reason: reviewReasons[claim.id] || "",
      });
      if (name === "reimburse") await api.post(`/tenant/expense-claims/${claim.id}/reimburse`, {
        payment_reference: paymentRefs[claim.id] || "",
      });
      toast.success(name === "reimburse" ? "Reimbursement recorded." : "Claim updated.");
      await load();
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const openReceipt = async (claim: Claim) => {
    try {
      const response = await api.get(`/tenant/expense-claims/${claim.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(response.data), "_blank", "noopener,noreferrer");
    } catch (error) { toastError(error); }
  };

  return <>
    <PageHeader title="Reimbursement Requests" subtitle={manager ? "Review requests, receipts, staff notes, and payment history" : "Create and track mileage, parking, meal, and other reimbursement requests"}
      actions={<Button onClick={() => { if (showForm) resetEditor(); else setShowForm(true); }}><Plus className="mr-2 h-4 w-4" /> New request</Button>} />
    <main className="mx-auto max-w-7xl space-y-5 p-4 md:p-8" data-testid="expense-claims">
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {["submitted", "approved", "rejected", "reimbursed"].map(key => <button key={key} onClick={() => setStatus(key)}
          className="rounded-lg border bg-white p-4 text-left hover:border-slate-400">
          <div className="text-xs capitalize text-slate-500">{key}</div>
          <div className="mt-1 text-xl font-semibold">{money(totals[key] || 0, "CAD")}</div>
        </button>)}
      </section>

      {showForm && <section className="rounded-lg border bg-white p-5">
        <h2 className="font-semibold">{editing ? "Edit reimbursement draft" : "New reimbursement request"}</h2>
        <p className="mb-4 text-xs text-slate-500">Drafts remain editable. Once submitted, the financial details are locked for review.</p>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-xs font-medium">Category<select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
            {["mileage", "parking", "meal", "other"].map(x => <option key={x} value={x}>{x === "other" ? "Others" : x}</option>)}</select></label>
          <label className="text-xs font-medium">Expense date<Input className="mt-1" type="date" value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} /></label>
          <label className="text-xs font-medium">Currency<Input className="mt-1" maxLength={3} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></label>
          {form.category === "mileage" ? <>
            <label className="text-xs font-medium">Claimed KM<Input className="mt-1" type="number" min="0" step="0.1" value={form.claimed_km} onChange={e => setForm({ ...form, claimed_km: e.target.value })} data-testid="claim-km" /></label>
            <label className="text-xs font-medium">Rate per KM<Input className="mt-1" type="number" min="0" step="0.01" value={form.km_rate} onChange={e => setForm({ ...form, km_rate: e.target.value })} data-testid="claim-km-rate" /></label>
          </> : <label className="text-xs font-medium">Amount<Input className="mt-1" type="number" min="0" step="0.01" value={form.expense_amount} onChange={e => setForm({ ...form, expense_amount: e.target.value })} data-testid="claim-expense-amount" /></label>}
          {(form.category === "mileage" || form.category === "parking" || form.category === "other") && <>
            <label className="text-xs font-medium">Activity period from<Input className="mt-1" type="date" value={form.period_start} max={form.period_end}
              onChange={e => setForm({ ...form, period_start: e.target.value, expense_date: e.target.value, visit_ids: [] })} /></label>
            <label className="text-xs font-medium">Activity period to<Input className="mt-1" type="date" value={form.period_end} min={form.period_start} max={today()}
              onChange={e => setForm({ ...form, period_end: e.target.value, visit_ids: [] })} /></label>
            <fieldset className="space-y-2 md:col-span-3" data-testid="claim-visit-select">
              <legend className="text-xs font-medium">Completed schedules / activities {form.category === "mileage" ? "*" : "(optional)"}</legend>
              {eligibleVisits.length ? eligibleVisits.map(visit => <label key={visit.id} className="flex items-start gap-2 rounded-md border p-3 text-sm">
                <input type="checkbox" className="mt-1" checked={form.visit_ids.includes(visit.id)}
                  onChange={e => setForm({ ...form, visit_ids: e.target.checked
                    ? [...form.visit_ids, visit.id]
                    : form.visit_ids.filter(id => id !== visit.id) })} />
                <span>{visitLabel(visit)}</span>
              </label>) : <p className="rounded-md border border-dashed p-3 text-xs text-slate-500">No completed schedules or activities are available in this period.</p>}
            </fieldset>
          </>}
          {(form.category === "meal" || form.category === "other") && <label className="text-xs font-medium">Related doctor / lead (optional)
            <select className="mt-1 h-10 w-full rounded-md border px-3 text-sm" value={form.contact_id}
              onChange={e => setForm({ ...form, contact_id: e.target.value })} data-testid="claim-contact-select">
              <option value="">No related doctor / lead</option>
              {contacts.map(contact => <option key={contact.id} value={contact.id}>{contact.first_name} {contact.last_name || ""}</option>)}
            </select>
          </label>}
          <label className="text-xs font-medium md:col-span-2">Notes / details{form.category !== "mileage" ? " *" : ""}<Input className="mt-1" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="claim-notes" /></label>
          <label className="text-xs font-medium md:col-span-2">Receipt (JPG, PNG, WebP or PDF; max 5 MB)
            <Input className="mt-1" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={e => setReceipt(e.target.files?.[0] || null)} /></label>
        </div>
        <div className="mt-4 flex gap-2"><Button disabled={busy} onClick={saveDraft}><WalletCards className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Save draft"}</Button>
          <Button variant="outline" onClick={resetEditor}>Cancel</Button></div>
      </section>}

      <section className="overflow-hidden rounded-lg border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
          <div><h2 className="font-semibold">{manager ? "Reimbursement approval queue" : "My reimbursement requests"}</h2>
            <p className="text-xs text-slate-500">Financial totals remain unchanged after submission.</p></div>
          <div className="flex gap-2"><select className="h-9 rounded-md border px-3 text-sm" value={status} onChange={e => setStatus(e.target.value)}>
            {["all", "draft", "submitted", "approved", "rejected", "reimbursed"].map(x => <option key={x} value={x}>{x === "submitted" ? "Pending approval" : x}</option>)}</select>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button></div>
        </div>
        <div className="divide-y">{items.map(claim => <article key={claim.id} className="space-y-3 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><div className="font-medium">{claim.claimant_name} · <span className="capitalize">{claim.category}</span></div>
              <div className="text-xs text-slate-500">{claim.expense_date} · {claim.category === "mileage" ? `${claim.claimed_km || 0} KM × ${claim.km_rate || 0}` : money(claim.expense_amount, claim.currency)}</div>
              {claim.period_start && <p className="mt-2 text-xs font-medium text-slate-500">Period: {claim.period_start} to {claim.period_end || claim.period_start}</p>}
              {claim.notes && <div className="mt-2"><div className="text-xs font-medium text-slate-500">Staff notes / details</div><p className="text-sm text-slate-700">{claim.notes}</p></div>}
              {!!claim.visit_snapshots?.length && <div className="mt-2"><div className="text-xs font-medium text-slate-500">Completed schedules / activities</div>
                <ul className="list-disc pl-5 text-sm text-slate-700">{claim.visit_snapshots.map(visit => <li key={visit.visit_id}>
                  {visit.title || [visit.contact_name, visit.organization_name].filter(Boolean).join(" — ") || "Completed schedule"} · {[visit.contact_name, visit.organization_name].filter(Boolean).join(" — ") || "No doctor or organization"} · {visit.visit_type} · {visit.start_at ? new Date(visit.start_at).toLocaleString() : visit.start_date}
                </li>)}</ul>
              </div>}
              {claim.review_reason && <p className="mt-2 text-sm text-rose-700">Reviewer: {claim.review_reason}</p>}</div>
            <div className="text-right"><div className="text-lg font-semibold">{money(claim.total_amount, claim.currency)}</div><Pill value={claim.status} type="status" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {claim.receipt_id && <Button size="sm" variant="outline" onClick={() => openReceipt(claim)}><FileText className="mr-2 h-4 w-4" /> {claim.receipt_name || "View receipt"}</Button>}
            {claim.status === "draft" && claim.claimant_user_id === user?.id && <Button size="sm" variant="outline" disabled={busy} onClick={() => beginEdit(claim)}><Pencil className="mr-2 h-4 w-4" /> Edit draft</Button>}
            {claim.status === "draft" && claim.claimant_user_id === user?.id && <Button size="sm" disabled={busy} onClick={() => action(claim, "submit")}><Upload className="mr-2 h-4 w-4" /> Submit for approval</Button>}
            {manager && claim.status === "submitted" && <>
              <Button size="sm" disabled={busy} onClick={() => action(claim, "approve")}><Check className="mr-2 h-4 w-4" /> Approve</Button>
              <Input className="h-9 max-w-xs" placeholder="Reason required to reject" value={reviewReasons[claim.id] || ""} onChange={e => setReviewReasons({ ...reviewReasons, [claim.id]: e.target.value })} />
              <Button size="sm" variant="outline" disabled={busy} onClick={() => action(claim, "reject")}><X className="mr-2 h-4 w-4" /> Reject</Button>
            </>}
            {owner && claim.status === "approved" && <>
              <Input className="h-9 max-w-xs" placeholder="Payment reference / transaction ID" value={paymentRefs[claim.id] || ""} onChange={e => setPaymentRefs({ ...paymentRefs, [claim.id]: e.target.value })} />
              <Button size="sm" disabled={busy} onClick={() => action(claim, "reimburse")}><WalletCards className="mr-2 h-4 w-4" /> Record reimbursed</Button>
            </>}
          </div>
        </article>)}</div>
        {!items.length && <div className="p-10 text-center text-sm text-slate-500">No claims match this filter.</div>}
      </section>
    </main>
  </>;
}
